import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, CheckCircle2, ChevronLeft, ChevronRight, Copy, FileText, Image as ImageIcon, Paperclip } from "lucide-react";
import { AppTaxCode, authedFetch, DuplicateMatch, ParseReportReason, QueueItem, quickbooks, Receipt, ReceiptAttachment, ReceiptListItem, receipts, ReceiptType, ReceiptUpdate, VendorSummary } from "../api/client";
import StatusPill from "../components/b/StatusPill";
import { readView, toApiFilters } from "./receiptFilters";

type Tab = "email" | "attachments";

const REPORT_REASONS: { value: ParseReportReason; label: string }[] = [
  { value: "wrong_vendor", label: "Wrong vendor / sender" },
  { value: "wrong_amount", label: "Wrong amount or currency" },
  { value: "wrong_date", label: "Wrong date (invoice or due)" },
  { value: "wrong_type", label: "Wrong document type (invoice / bill / payment)" },
  { value: "other", label: "Something else" },
];

const TYPE_OPTIONS: { value: ReceiptType; label: string }[] = [
  { value: "receipt", label: "Receipt" },
  { value: "bill", label: "Bill" },
  { value: "income", label: "Income" },
];

const TYPE_LABEL: Record<ReceiptType, string> = {
  receipt: "Receipt",
  bill: "Bill",
  income: "Income",
};

const MATCH_LABEL: Record<DuplicateMatch, string> = {
  invoice: "same vendor and invoice number",
  transaction: "same card, date, amount and sender domain",
  payment: "payment: same vendor and amount",
};

function formatCents(cents?: number | null, currency?: string | null): string {
  if (cents == null) return "—";
  const value = cents / 100;
  try {
    return value.toLocaleString("en-US", { style: "currency", currency: currency || "USD" });
  } catch {
    return value.toFixed(2);
  }
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString();
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Cents → a plain dollars string for the amount <input>. */
function centsToInput(cents?: number | null): string {
  if (cents == null) return "";
  return (cents / 100).toFixed(2);
}

interface EditForm {
  vendor_name: string; // free text, with existing vendors suggested
  amount: string; // dollars, as typed
  tax: string; // dollars, as typed
  currency: string;
  invoice_number: string;
  invoice_date: string; // YYYY-MM-DD
  due_date: string; // YYYY-MM-DD
  card_brand: string;
  card_last4: string;
  description: string;
  tax_code: string; // "" = detect from the tax
}

/** Renders one uploaded attachment. The bytes live behind an authenticated
 *  endpoint, so we can't point an <iframe>/<img>/<a> straight at the URL —
 *  the browser wouldn't send our Bearer token and the request 401s. Instead
 *  we fetch via authedFetch and render from an object URL. */
function AttachmentBlock({ receiptId, attachment }: { receiptId: string; attachment: ReceiptAttachment }) {
  const a = attachment;
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  // The server corrects generic labels (application/octet-stream) from the file
  // extension, so trust the type it serves over the stored metadata.
  const [mime, setMime] = useState(a.content_type);
  const isPdf = mime.startsWith("application/pdf");
  const isImage = mime.startsWith("image/");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setBlobUrl(null);
    setErr(null);
    (async () => {
      try {
        const res = await authedFetch(receipts.attachmentUrl(receiptId, a.file_id));
        if (!res.ok) throw new Error(`Could not load attachment (${res.status})`);
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        if (blob.type) setMime(blob.type);
        setBlobUrl(objectUrl);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Could not load attachment");
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [receiptId, a.file_id]);

  return (
    <div className="attachment-block">
      <div className="attachment-header">
        <span style={{ color: "var(--text-soft)", display: "inline-flex" }}>
            {isPdf ? <FileText size={20} /> : isImage ? <ImageIcon size={20} /> : <Paperclip size={20} />}
          </span>
        <div className="attachment-name">{a.filename}</div>
        <div className="attachment-meta">
          {mime} · {formatBytes(a.size_bytes)}
        </div>
        {/* Only PDFs and raster images: an opened blob runs with this site's origin. */}
        {blobUrl && (isPdf || (isImage && !mime.includes("svg"))) && (
          <a href={blobUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
            Open in new tab
          </a>
        )}
      </div>
      {err ? (
        <div className="page-error">{err}</div>
      ) : !blobUrl ? (
        <div className="page-empty">Loading attachment…</div>
      ) : isPdf ? (
        <iframe className="pdf-viewer" src={blobUrl} title={a.filename} />
      ) : isImage ? (
        <img className="attachment-image" src={blobUrl} alt={a.filename} />
      ) : (
        <a href={blobUrl} download={a.filename} className="btn btn-primary btn-sm">
          Download {a.filename}
        </a>
      )}
    </div>
  );
}

export default function ReceiptDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // The list's filters ride along in the URL, so this page can step through
  // the same list and "Back" returns to it.
  const [params] = useSearchParams();
  const listSearch = params.toString() ? `?${params}` : "";
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [queue, setQueue] = useState<QueueItem[] | null>(null);
  const [caughtUp, setCaughtUp] = useState(false);
  // The receipt just marked reviewed by "reviewed & next", for the Undo toast.
  const [undoId, setUndoId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("email");
  const [acting, setActing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [qboMsg, setQboMsg] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<EditForm | null>(null);
  // Existing vendors across the user's receipts — suggestions for the vendor
  // field so a correction lands on an existing vendor's exact spelling.
  const [vendors, setVendors] = useState<VendorSummary[]>([]);
  // "This was parsed wrong" report modal.
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState<ParseReportReason>("wrong_vendor");
  const [reportDetails, setReportDetails] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  // True once the user has an existing report on this receipt (badge + disables
  // the report button so they don't file duplicates).
  const [reported, setReported] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Hidden copies that point at this receipt.
  const [duplicates, setDuplicates] = useState<ReceiptListItem[]>([]);
  const [taxCodes, setTaxCodes] = useState<AppTaxCode[]>([]);
  useEffect(() => {
    receipts.taxCodes().then(setTaxCodes).catch(() => undefined);
  }, []);
  const payments = duplicates.filter((d) => d.duplicate_match === "payment");
  const copies = duplicates.filter((d) => d.duplicate_match !== "payment");

  function setField<K extends keyof EditForm>(k: K, v: string) {
    setForm((f) => (f ? { ...f, [k]: v } : f));
  }

  async function submitReport(e: FormEvent) {
    e.preventDefault();
    if (!receipt) return;
    setReportSubmitting(true);
    setReportError(null);
    try {
      await receipts.report(receipt.id, {
        reason: reportReason,
        details: reportDetails.trim() || null,
      });
      setReported(true);
      setReportOpen(false);
      setReportDetails("");
      setReportReason("wrong_vendor");
    } catch (err) {
      setReportError(err instanceof Error ? err.message : "Could not submit report");
    } finally {
      setReportSubmitting(false);
    }
  }

  function startEdit() {
    if (!receipt) return;
    setForm({
      vendor_name: receipt.vendor_name || "",
      amount: centsToInput(receipt.amount_cents),
      tax: centsToInput(receipt.tax_cents),
      currency: receipt.currency || "",
      invoice_number: receipt.invoice_number || "",
      invoice_date: receipt.invoice_date || "",
      due_date: receipt.due_date || "",
      card_brand: receipt.card_brand || "",
      card_last4: receipt.card_last4 || "",
      description: receipt.description || "",
      tax_code: receipt.tax_code_manual ? receipt.tax_code || "" : "",
    });
    setError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setForm(null);
    setError(null);
  }

  async function saveEdit() {
    if (!receipt || !form) return;
    // Amount: blank clears it; otherwise must be a finite number.
    const amountTrim = form.amount.trim();
    let total_cents: number | null = null;
    if (amountTrim !== "") {
      const parsed = Number(amountTrim);
      if (!Number.isFinite(parsed)) {
        setError("Amount must be a number.");
        return;
      }
      total_cents = Math.round(parsed * 100);
    }
    const taxTrim = form.tax.trim();
    let tax_cents: number | null = null;
    if (taxTrim !== "") {
      const parsed = Number(taxTrim);
      if (!Number.isFinite(parsed)) {
        setError("Tax must be a number.");
        return;
      }
      tax_cents = Math.round(parsed * 100);
    }
    const last4 = form.card_last4.trim();
    if (last4 && !/^\d{1,4}$/.test(last4)) {
      setError("Card last 4 must be up to 4 digits.");
      return;
    }
    const body: ReceiptUpdate = {
      total_cents,
      tax_cents,
      description: form.description.trim() || null,
      currency: form.currency.trim().toUpperCase() || null,
      invoice_number: form.invoice_number.trim() || null,
      invoice_date: form.invoice_date || null,
      due_date: form.due_date || null,
      card_brand: form.card_brand.trim() || null,
      card_last4: last4 || null,
    };
    if ((form.tax_code || null) !== (receipt.tax_code_manual ? receipt.tax_code ?? null : null)) {
      body.tax_code = form.tax_code || null;
    }
    const selectedVendor = form.vendor_name.trim();
    if (selectedVendor !== (receipt.vendor_name || "")) {
      body.vendor_name = selectedVendor || null;
    }
    setActing(true);
    setError(null);
    try {
      const updated = await receipts.update(receipt.id, body);
      setReceipt(updated);
      setEditing(false);
      setForm(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setActing(false);
    }
  }

  async function load() {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const r = await receipts.get(id);
      setReceipt(r);
      // Checking a receipt means comparing it with the document, so lead with it.
      setTab((r.attachment_file_ids || []).length > 0 ? "attachments" : "email");
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Could not load receipt");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setEditing(false);
    setForm(null);
    setError(null);
    setNotice(null);
    setQboMsg(null);
    setCaughtUp(false);
    setReportOpen(false);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // The list this receipt was opened from (same filters), for prev/next and the
  // review queue. Fetched once per filter set, not per receipt.
  useEffect(() => {
    receipts
      .queue(toApiFilters(readView(params)))
      .then(setQueue)
      .catch(() => setQueue(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.toString()]);

  useEffect(() => {
    if (!undoId) return;
    const t = setTimeout(() => setUndoId(null), 8000);
    return () => clearTimeout(t);
  }, [undoId]);

  // Vendor dropdown options — best-effort; a failure just leaves the picker
  // showing only the receipt's current vendor.
  useEffect(() => {
    receipts.listVendors().then(setVendors).catch(() => {});
  }, []);

  useEffect(() => {
    setDuplicates([]);
    if (!id) return;
    receipts.duplicates(id).then(setDuplicates).catch(() => {});
  }, [id]);

  // Has the user already reported this receipt? Best-effort — drives the badge.
  useEffect(() => {
    if (!id) return;
    receipts
      .listReports(id)
      .then((rs) => setReported(rs.length > 0))
      .catch(() => {});
  }, [id]);

  const index = queue && receipt ? queue.findIndex((q) => q.id === receipt.id) : -1;
  const prevId = queue && index > 0 ? queue[index - 1].id : null;
  const nextId = queue && index >= 0 && index < queue.length - 1 ? queue[index + 1].id : null;
  const leftToReview = queue ? queue.filter((q) => q.status === "pending").length : 0;

  function go(target: string) {
    navigate(`/app/receipts/${target}${listSearch}`);
  }

  /** The next receipt still needing review after `fromId`, wrapping to the top. */
  function nextPending(fromId: string): string | null {
    if (!queue) return null;
    const i = queue.findIndex((q) => q.id === fromId);
    const hit = queue.slice(i + 1).find((q) => q.status === "pending")
      ?? queue.slice(0, Math.max(i, 0)).find((q) => q.status === "pending");
    return hit?.id ?? null;
  }

  function setQueueStatus(target: string, status: Receipt["status"]) {
    setQueue((q) => q && q.map((x) => (x.id === target ? { ...x, status } : x)));
  }

  async function markStatus(status: "reviewed" | "pending") {
    if (!receipt) return;
    setActing(true);
    setError(null);
    try {
      const updated = await receipts.update(receipt.id, { status });
      setQueueStatus(receipt.id, updated.status);
      if (status === "reviewed" && index >= 0) {
        const next = nextPending(receipt.id);
        if (next) {
          setUndoId(receipt.id);
          go(next);
          return;
        }
        setCaughtUp(true);
      }
      setReceipt(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed");
    } finally {
      setActing(false);
    }
  }

  async function undoReviewed() {
    if (!undoId) return;
    const target = undoId;
    setUndoId(null);
    try {
      await receipts.update(target, { status: "pending" });
      setQueueStatus(target, "pending");
      go(target);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Undo failed");
    }
  }

  // Keyboard review: R reviewed & next, J/K next/previous, E edit, Esc back.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey || !receipt) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable='true']")) return;
      if (reportOpen) {
        if (e.key === "Escape") setReportOpen(false);
        return;
      }
      if (editing) {
        if (e.key === "Escape") cancelEdit();
        return;
      }
      const key = e.key.toLowerCase();
      if (key === "r" && !acting) {
        if (receipt.status !== "reviewed") markStatus("reviewed");
        else {
          const next = nextPending(receipt.id);
          if (next) go(next);
        }
      } else if (key === "j" && nextId) go(nextId);
      else if (key === "k" && prevId) go(prevId);
      else if (key === "e" && !acting) startEdit();
      else if (key === "escape") navigate(`/app/receipts${listSearch}`);
      else return;
      e.preventDefault();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  async function changeType(receipt_type: ReceiptType) {
    if (!receipt || receipt_type === receipt.receipt_type) return;
    setActing(true);
    setError(null);
    try {
      const updated = await receipts.update(receipt.id, { receipt_type });
      setReceipt(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed");
    } finally {
      setActing(false);
    }
  }

  async function handleReparse() {
    if (!receipt) return;
    if (!confirm("Send this receipt's email/upload through the AI again? Manual edits to it will be replaced.")) return;
    setActing(true);
    setError(null);
    try {
      const res = await receipts.reparse(receipt.id);
      // The worker replaces this receipt with a new one, so this page is about to go stale.
      navigate(`/app/receipts${listSearch}`, {
        state: { notice: `${res.message} The refreshed receipt appears in the Ledger in a minute.` },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reparse failed");
    } finally {
      setActing(false);
    }
  }

  /** "Not a duplicate": show the copy on its own; the server reparses it. */
  async function splitOut(copyId: string) {
    if (!confirm("Show this as its own receipt? It won't be hidden or linked to another one again, and its email will be read by the AI again.")) return;
    setActing(true);
    setError(null);
    try {
      const res = await receipts.split(copyId);
      setNotice(res.queued
        ? `${res.message} It's now its own receipt; the refreshed version will replace it in a minute.`
        : res.message);
      if (receipt && copyId === receipt.id) await load();
      else setDuplicates((d) => d.filter((x) => x.id !== copyId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not split");
    } finally {
      setActing(false);
    }
  }

  async function handleDelete() {
    if (!receipt) return;
    if (!confirm("Delete this receipt? This cannot be undone.")) return;
    setActing(true);
    try {
      await receipts.remove(receipt.id);
      const neighbour = nextId || prevId;
      setQueue((q) => q && q.filter((x) => x.id !== receipt.id));
      if (neighbour) go(neighbour);
      else navigate(`/app/receipts${listSearch}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setActing(false);
    }
  }

  async function syncToQbo() {
    if (!receipt) return;
    setSyncing(true);
    setQboMsg(null);
    try {
      const res = await quickbooks.syncReceipt(receipt.id);
      if (res.status === "ok") {
        setReceipt({
          ...receipt,
          qbo_sync_status: "synced",
          qbo_synced_at: new Date().toISOString(),
          qbo_sync_error: null,
        });
        setQboMsg(`Synced to QuickBooks as a ${res.qbo_entity_type ?? "record"}.`);
      } else if (res.status === "error") {
        setReceipt({ ...receipt, qbo_sync_status: "error", qbo_sync_error: res.message ?? null });
        setQboMsg(res.message || "Sync failed.");
      } else {
        setQboMsg(res.message || "Nothing to sync.");
      }
    } catch (err) {
      // 404 = no QuickBooks connection configured.
      setQboMsg(
        err instanceof Error && /not found|404/i.test(err.message)
          ? "Connect QuickBooks first (Integrations → QuickBooks)."
          : err instanceof Error
            ? err.message
            : "Sync failed",
      );
    } finally {
      setSyncing(false);
    }
  }

  // Keep the current receipt on screen while the next one loads (no flash).
  if (loading && !receipt) return <div className="page-empty">Loading…</div>;
  if (loadError || !receipt) return (
    <div className="page">
      <div className="page-error">{loadError || "Receipt not found"}</div>
      <Link to={`/app/receipts${listSearch}`} className="btn btn-ghost">← Back to receipts</Link>
    </div>
  );

  const bodyText = receipt.body_text_full || "";
  const bodyHtml = receipt.body_html_full;
  const attachments = receipt.attachment_file_ids || [];
  // Attachments the email had whose bytes weren't downloaded (too large)
  // or not fetched by the provider. We still want
  // to show them so the user knows the email has, say, an `invoice.pdf`
  // attachment they should pull from their inbox.
  const uploadedNames = new Set(attachments.map((a) => a.filename));
  const metaOnlyAttachments = (receipt.attachments_meta || []).filter(
    (m) => m.filename && !uploadedNames.has(m.filename),
  );
  const totalAttachmentCount = attachments.length + metaOnlyAttachments.length;

  return (
    <div className={"page page-wide review-page" + (loading ? " is-loading" : "")}>
      <div className="review-bar">
        <Link to={`/app/receipts${listSearch}`} className="btn btn-ghost btn-sm">
          <ArrowLeft size={14} /> Ledger
        </Link>
        {queue && index >= 0 && (
          <div className="review-nav">
            <button className="icon-btn" onClick={() => prevId && go(prevId)} disabled={!prevId}
              title="Previous (K)" aria-label="Previous receipt">
              <ChevronLeft size={16} />
            </button>
            <span className="num">{index + 1} of {queue.length}</span>
            <button className="icon-btn" onClick={() => nextId && go(nextId)} disabled={!nextId}
              title="Next (J)" aria-label="Next receipt">
              <ChevronRight size={16} />
            </button>
          </div>
        )}
        {queue && index >= 0 && (
          <span className="review-left">
            {leftToReview === 0 ? "All reviewed" : `${leftToReview} to review`}
          </span>
        )}
        <span className="kbd-hints" aria-hidden="true">
          <span><kbd>R</kbd> reviewed &amp; next</span>
          <span><kbd>J</kbd><kbd>K</kbd> next / previous</span>
          <span><kbd>E</kbd> edit</span>
          <span><kbd>Esc</kbd> back</span>
        </span>
      </div>

      <div className="page-header">
        <h1>{receipt.vendor_name || receipt.sender || "Receipt"}</h1>
        <StatusPill status={receipt.receipt_type} label={TYPE_LABEL[receipt.receipt_type] ?? receipt.receipt_type} />
        <StatusPill status={receipt.status} />
        {payments.length > 0 && (
          <button className="btn btn-ghost btn-sm" title="Show the payment linked to this bill"
            onClick={() => document.getElementById("dup-list")?.scrollIntoView({ behavior: "smooth", block: "nearest" })}>
            <CheckCircle2 size={14} /> Paid
          </button>
        )}
        {copies.length > 0 && (
          <button className="btn btn-ghost btn-sm" title="Show the hidden copies of this receipt"
            onClick={() => document.getElementById("dup-list")?.scrollIntoView({ behavior: "smooth", block: "nearest" })}>
            <Copy size={14} /> {copies.length} duplicate{copies.length === 1 ? "" : "s"}
          </button>
        )}
        {editing ? (
          <>
            <button onClick={saveEdit} className="btn btn-primary btn-sm" disabled={acting}>
              {acting ? "Saving…" : "Save"}
            </button>
            <button onClick={cancelEdit} className="btn btn-ghost btn-sm" disabled={acting}>
              Cancel
            </button>
          </>
        ) : (
          <>
            {receipt.qbo_sync_status !== "synced" && (
              <button onClick={handleReparse} className="btn btn-ghost btn-sm" disabled={acting}
                title="Run the AI parser on the original email/upload again">
                Reparse with AI
              </button>
            )}
            <button onClick={syncToQbo} className="btn btn-ghost btn-sm" disabled={acting || syncing}>
              {syncing
                ? "Syncing…"
                : receipt.qbo_sync_status === "synced"
                  ? "✓ Synced to QuickBooks"
                  : "Sync to QuickBooks"}
            </button>
            <button onClick={handleDelete} className="btn btn-danger btn-sm" disabled={acting}>
              Delete
            </button>
            {reported ? (
              <span className="value muted" title="You've reported this receipt. Our team will review it.">
                ⚑ Reported
              </span>
            ) : (
              <button
                onClick={() => { setReportError(null); setReportOpen(true); }}
                className="btn btn-ghost btn-sm"
                disabled={acting}
                title="Tell us this receipt's details were parsed incorrectly"
              >
                Report incorrect parsing
              </button>
            )}
            <button onClick={startEdit} className="btn btn-ghost btn-sm" disabled={acting}>
              Edit
            </button>
            {receipt.status !== "reviewed" ? (
              <button onClick={() => markStatus("reviewed")} className="btn btn-primary btn-sm" disabled={acting}
                title={index >= 0 ? "Mark reviewed and open the next one (R)" : "Mark reviewed (R)"}>
                <Check size={14} /> {index >= 0 ? "Reviewed & next" : "Mark reviewed"}
              </button>
            ) : (
              <button onClick={() => markStatus("pending")} className="btn btn-ghost btn-sm" disabled={acting}>
                Mark unreviewed
              </button>
            )}
          </>
        )}
      </div>

      {caughtUp && (
        <div className="caught-up">
          <CheckCircle2 size={18} />
          <span>You're all caught up. Nothing else in this list needs review.</span>
          <Link to={`/app/receipts${listSearch}`} className="btn btn-ghost btn-sm">Back to receipts</Link>
        </div>
      )}
      {error && <div className="page-error">{error}</div>}

      {notice && <div className="muted" style={{ marginBottom: 12 }}>{notice}</div>}

      {(qboMsg || receipt.qbo_sync_status === "error") && (
        <div
          className={receipt.qbo_sync_status === "error" ? "page-error" : "muted"}
          style={{ marginBottom: 12 }}
        >
          {qboMsg || receipt.qbo_sync_error}
        </div>
      )}

      {receipt.duplicate_of_id && receipt.duplicate_match === "payment" ? (
        <div className="dup-banner">
          <CheckCircle2 size={16} />
          <span>
            The payment of{" "}
            <Link to={`/app/receipts/${receipt.duplicate_of_id}${listSearch}`}>a bill</Link> from the same vendor for
            the same amount. Hidden so the expense isn't counted twice; the bill stands for both.
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => splitOut(receipt.id)} disabled={acting}>
            Not this bill's payment
          </button>
        </div>
      ) : receipt.duplicate_of_id && (
        <div className="dup-banner">
          <Copy size={16} />
          <span>
            Hidden as a duplicate of{" "}
            <Link to={`/app/receipts/${receipt.duplicate_of_id}${listSearch}`}>another receipt</Link>
            {receipt.duplicate_match ? ` (${MATCH_LABEL[receipt.duplicate_match]})` : ""}.
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => splitOut(receipt.id)} disabled={acting}>
            Not a duplicate
          </button>
        </div>
      )}

      {receipt.is_sample && (
        <div className="page-toast">
          This is a sample receipt for the tour. It's deleted when the tour ends and never goes to QuickBooks.
        </div>
      )}

      <div className="review-grid">
      <div className="detail-card" data-tour="receipt-fields">
        <div className="detail-row">
          <span className="label">Type</span>
          <span className="value">
            <select
              value={receipt.receipt_type}
              onChange={(e) => changeType(e.target.value as ReceiptType)}
              disabled={acting || editing}
              aria-label="Receipt type"
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </span>
        </div>
        <div className="detail-row">
          <span className="label">Vendor</span>
          <span className="value">
            {editing && form ? (
              <>
                <input
                  type="text"
                  list="vendor-suggestions"
                  value={form.vendor_name}
                  onChange={(e) => setField("vendor_name", e.target.value)}
                  disabled={acting}
                  aria-label="Vendor"
                />
                <datalist id="vendor-suggestions">
                  {vendors.map((v) => (
                    <option key={v.vendor_name} value={v.vendor_name} />
                  ))}
                </datalist>
              </>
            ) : (
              receipt.vendor_name || "—"
            )}
          </span>
        </div>
        <div className="detail-row">
          <span className="label">Amount</span>
          <span className="value">
            {editing && form ? (
              <input
                type="number"
                step="0.01"
                inputMode="decimal"
                placeholder="0.00"
                value={form.amount}
                onChange={(e) => setField("amount", e.target.value)}
                disabled={acting}
                style={{ width: 140 }}
                aria-label="Amount"
              />
            ) : (
              formatCents(receipt.amount_cents, receipt.currency)
            )}
          </span>
        </div>
        <div className="detail-row">
          <span className="label">Tax</span>
          <span className="value">
            {editing && form ? (
              <input
                type="number"
                step="0.01"
                inputMode="decimal"
                placeholder="0.00"
                value={form.tax}
                onChange={(e) => setField("tax", e.target.value)}
                disabled={acting}
                style={{ width: 140 }}
                aria-label="Tax"
              />
            ) : (
              <>
                {formatCents(receipt.tax_cents, receipt.currency)}
                {receipt.tax_breakdown && receipt.tax_breakdown.length > 0 && (
                  <span className="muted tax-parts">
                    {receipt.tax_breakdown.map((t, i) => (
                      <span key={i}>
                        {t.type === "US_SALES_TAX" ? "US sales tax" : t.type}
                        {t.rate != null ? ` ${t.rate}%` : ""}
                        {t.amount_cents != null ? ` ${formatCents(t.amount_cents, receipt.currency)}` : ""}
                      </span>
                    ))}
                  </span>
                )}
              </>
            )}
          </span>
        </div>
        <div className="detail-row">
          <span className="label">Tax code</span>
          <span className="value">
            {editing && form ? (
              <select value={form.tax_code} onChange={(e) => setField("tax_code", e.target.value)} disabled={acting}
                aria-label="Tax code">
                <option value="">Detect from the tax{receipt.tax_code && !receipt.tax_code_manual
                  ? ` (${taxCodes.find((c) => c.key === receipt.tax_code)?.label ?? receipt.tax_code})` : ""}</option>
                {taxCodes.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            ) : (
              <>
                {taxCodes.find((c) => c.key === receipt.tax_code)?.label ?? receipt.tax_code ?? "—"}
                <span className="muted" style={{ marginLeft: 8, fontSize: 13 }}>
                  {receipt.tax_code_manual ? "set by you" : "detected"}
                </span>
              </>
            )}
          </span>
        </div>
        <div className="detail-row">
          <span className="label">Currency</span>
          <span className="value">
            {editing && form ? (
              <input
                type="text"
                maxLength={8}
                placeholder="USD"
                value={form.currency}
                onChange={(e) => setField("currency", e.target.value.toUpperCase())}
                disabled={acting}
                style={{ width: 100 }}
                aria-label="Currency"
              />
            ) : (
              receipt.currency || "—"
            )}
          </span>
        </div>
        <div className="detail-row">
          <span className="label">Invoice #</span>
          <span className="value">
            {editing && form ? (
              <input
                type="text"
                value={form.invoice_number}
                onChange={(e) => setField("invoice_number", e.target.value)}
                disabled={acting}
                aria-label="Invoice number"
              />
            ) : (
              receipt.invoice_number || "—"
            )}
          </span>
        </div>
        <div className="detail-row">
          <span className="label">Invoice date</span>
          <span className="value">
            {editing && form ? (
              <input
                type="date"
                value={form.invoice_date}
                onChange={(e) => setField("invoice_date", e.target.value)}
                disabled={acting}
                aria-label="Invoice date"
              />
            ) : (
              receipt.invoice_date || "—"
            )}
          </span>
        </div>
        <div className="detail-row">
          <span className="label">Due date</span>
          <span className="value">
            {editing && form ? (
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setField("due_date", e.target.value)}
                disabled={acting}
                aria-label="Due date"
              />
            ) : (
              receipt.due_date || "—"
            )}
          </span>
        </div>
        <div className="detail-row">
          <span className="label">Card</span>
          <span className="value">
            {editing && form ? (
              <span style={{ display: "inline-flex", gap: 6 }}>
                <input
                  type="text"
                  placeholder="Visa"
                  value={form.card_brand}
                  onChange={(e) => setField("card_brand", e.target.value)}
                  disabled={acting}
                  style={{ width: 110 }}
                  aria-label="Card brand"
                />
                <input
                  type="text"
                  placeholder="1234"
                  maxLength={4}
                  inputMode="numeric"
                  value={form.card_last4}
                  onChange={(e) => setField("card_last4", e.target.value.replace(/[^0-9]/g, ""))}
                  disabled={acting}
                  style={{ width: 80 }}
                  aria-label="Card last 4"
                />
              </span>
            ) : receipt.card_last4 ? (
              <Link to={`/app/cards/${receipt.card_last4}`}>
                {(receipt.card_brand ?? "Card") + " ···· " + receipt.card_last4}
              </Link>
            ) : (
              "—"
            )}
          </span>
        </div>
        <div className="detail-row">
          <span className="label">Description</span>
          <span className="value">
            {editing && form ? (
              <input
                type="text"
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                disabled={acting}
                aria-label="Description"
              />
            ) : (
              receipt.description || "—"
            )}
          </span>
        </div>
        <div className="detail-row">
          <span className="label">AI confidence</span>
          <span className="value">
            {receipt.ai_confidence != null ? `${Math.round(receipt.ai_confidence * 100)}%` : "—"}
            <span className="muted"> · {receipt.source === "upload" ? "from an upload" : "from email"}</span>
          </span>
        </div>
        <div className="detail-row">
          <span className="label">Received</span>
          <span className="value">{formatDateTime(receipt.received_at)}</span>
        </div>
        <div className="detail-row">
          <span className="label">Subject</span>
          <span className="value">{receipt.subject || "—"}</span>
        </div>
        <div className="detail-row">
          <span className="label">Sender</span>
          <span className="value">{receipt.sender || "—"}</span>
        </div>
        {(() => {
          const to = receipt.recipients_to ?? [];
          const cc = receipt.recipients_cc ?? [];
          const fmt = (r: { email: string; name?: string | null }) =>
            r.name ? `${r.name} <${r.email}>` : r.email;
          if (to.length === 0 && cc.length === 0) return null;
          return (
            <>
              {to.length > 0 && (
                <div className="detail-row">
                  <span className="label">To</span>
                  <span className="value">{to.map(fmt).join(", ")}</span>
                </div>
              )}
              {cc.length > 0 && (
                <div className="detail-row">
                  <span className="label">Cc</span>
                  <span className="value">{cc.map(fmt).join(", ")}</span>
                </div>
              )}
            </>
          );
        })()}
        <div className="detail-row">
          <span className="label">Attachments</span>
          <span className="value">
            {totalAttachmentCount === 0 ? (
              "—"
            ) : (
              <button
                type="button"
                onClick={() => setTab("attachments")}
                className="btn btn-ghost btn-sm"
                style={{ padding: "0 6px" }}
                title="Jump to the Attachments tab"
              >
                <Paperclip size={13} style={{ verticalAlign: "-2px" }} /> {totalAttachmentCount}{" "}
                {totalAttachmentCount === 1 ? "file" : "files"}
                {[...attachments, ...metaOnlyAttachments]
                  .slice(0, 2)
                  .map((a) => ` · ${a.filename}`)
                  .join("")}
                {totalAttachmentCount > 2
                  ? ` · +${totalAttachmentCount - 2} more`
                  : ""}
              </button>
            )}
          </span>
        </div>
        {duplicates.length > 0 && (
          <div className="dup-list" id="dup-list">
            <h4>
              <Copy size={14} />{" "}
              {[
                payments.length > 0 && "Payment",
                copies.length > 0 && `${copies.length} hidden duplicate${copies.length === 1 ? "" : "s"}`,
              ].filter(Boolean).join(" and ")}
            </h4>
            {duplicates.map((d) => (
              <div key={d.id} className="dup-item">
                <Link to={`/app/receipts/${d.id}${listSearch}`} className="dup-what">
                  <span>{d.subject || d.vendor_name || "Receipt"}</span>
                  <span className="muted">
                    {formatDateTime(d.received_at)} · {formatCents(d.amount_cents, d.currency)}
                    {d.duplicate_match ? ` · ${MATCH_LABEL[d.duplicate_match]}` : ""}
                  </span>
                </Link>
                <button className="btn btn-ghost btn-sm" onClick={() => splitOut(d.id)} disabled={acting}>
                  {d.duplicate_match === "payment" ? "Not this bill's payment" : "Not a duplicate"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="review-source" data-tour="receipt-source">
      <div className="tabs">
        <button
          className={"tab" + (tab === "email" ? " active" : "")}
          onClick={() => setTab("email")}
          type="button"
        >
          Email
        </button>
        <button
          className={"tab" + (tab === "attachments" ? " active" : "")}
          onClick={() => setTab("attachments")}
          type="button"
        >
          Attachments {totalAttachmentCount > 0 && `(${totalAttachmentCount})`}
        </button>
      </div>

      {tab === "email" && (
        <div className="detail-card source-pane">
          <h3 style={{ marginTop: 0 }}>Message</h3>
          {bodyHtml ? (
            <iframe
              className="email-iframe"
              sandbox="allow-same-origin"
              srcDoc={bodyHtml}
              title="Email body"
            />
          ) : bodyText ? (
            <pre className="email-body">{bodyText}</pre>
          ) : (
            <div className="page-empty">No message body captured.</div>
          )}
        </div>
      )}

      {tab === "attachments" && (
        <div className="source-pane source-files">
          {totalAttachmentCount === 0 ? (
            <div className="page-empty">No attachments on this receipt.</div>
          ) : (
            <>
              {attachments.map((a) => (
                <AttachmentBlock key={a.file_id} receiptId={receipt.id} attachment={a} />
              ))}
              {metaOnlyAttachments.map((m, idx) => {
                const ct = m.content_type || "";
                const isPdf = ct.startsWith("application/pdf");
                const isImage = ct.startsWith("image/");
                return (
                  <div
                    key={`meta-${idx}-${m.filename}`}
                    className="attachment-block attachment-block-meta"
                  >
                    <div className="attachment-header">
                      <span style={{ color: "var(--text-soft)", display: "inline-flex" }}>
            {isPdf ? <FileText size={20} /> : isImage ? <ImageIcon size={20} /> : <Paperclip size={20} />}
          </span>
                      <div className="attachment-name">{m.filename || "Attachment"}</div>
                      <div className="attachment-meta">
                        {ct || "unknown"}
                        {m.size_bytes ? ` · ${formatBytes(m.size_bytes)}` : ""}
                        {" · not downloadable (open the original email)"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
      </div>
      </div>

      {undoId && (
        <div className="toast" role="status">
          Marked reviewed
          <button type="button" className="toast-action" onClick={undoReviewed}>Undo</button>
        </div>
      )}

      {reportOpen && (
        <div className="modal-backdrop" onClick={() => !reportSubmitting && setReportOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Report incorrect parsing</h2>
            <p style={{ color: "var(--text-soft)", fontSize: 14, marginBottom: 16 }}>
              Tell us what's wrong with this receipt's extracted details. An
              administrator will review it and reparse it.
            </p>
            <form onSubmit={submitReport}>
              <label>
                What's wrong?
                <select
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value as ParseReportReason)}
                  disabled={reportSubmitting}
                >
                  {REPORT_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </label>
              <label>
                More details (optional)
                <textarea
                  rows={4}
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                  maxLength={4000}
                  placeholder="e.g. the total should be $42.00, not $420.00"
                  disabled={reportSubmitting}
                />
              </label>
              {reportError && <div className="page-error" style={{ marginTop: 8 }}>{reportError}</div>}
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setReportOpen(false)}
                  className="btn btn-ghost"
                  disabled={reportSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={reportSubmitting}>
                  {reportSubmitting ? "Submitting…" : "Submit report"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
